import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import InvDataGrid from '../InvDataGrid';
import StatusChip from '../StatusChip';
import InvButton from '../InvButton';
import ConfirmDialog from '../ConfirmDialog';
import FilterToolbar from '../FilterToolbar';
import SectionCard from '../SectionCard';
import InvTabBar from '../InvTabBar';
import {
  getGoodsDiscards,
  getPendingGoodsDiscards,
  approveGoodsDiscard,
  rejectGoodsDiscard,
  cancelGoodsDiscard,
} from '../../api/goodsDiscard';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { GOODS_DISCARD_STATUSES } from '../../constants/storeManagement';
import { storeDisplayName } from '../../utils/storeEntity';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { formatDate, formatDateTime } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const LIST_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'all', label: 'All' },
];

/** ConfirmDialog action keys on this panel → the permission each one needs. */
const CONFIRM_ACTION_PERMISSION = {
  approve: INV_ACTIONS.DISCARD_APPROVE,
  reject: INV_ACTIONS.DISCARD_REJECT,
  cancel: INV_ACTIONS.DISCARD_CANCEL,
};

export default function GoodsDiscardListPanel() {
  const history = useHistory();
  const { can, myStoreId, myStoreIds } = useInvRole();
  const { url } = useRouteMatch();
  const base = url.replace(/\/goods-discard\/?$/, '/goods-discard');

  const [listTab, setListTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A store-scoped user (Store Keeper) only sees their own store, so seed the filter with
  // it rather than "All stores". myStoreId is settled on first render: InvRoleRoute holds
  // the spinner until permissions resolve.
  const storeLocked = myStoreIds.length === 1;
  const [storeId, setStoreId] = useState(myStoreId || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const storeName = useCallback((id) => {
    if (!id) return '—';
    const s = stores.find(r => (r.store_id || r.id) === id);
    return s ? storeDisplayName(s) : id;
  }, [stores]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (storeId) params.store_id = storeId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      let data;
      if (listTab === 'pending') {
        // API: GET /inventory/goods-discard/pending/
        data = await getPendingGoodsDiscards(params);
      } else {
        if (statusFilter) params.status = statusFilter;
        // API: GET /inventory/goods-discard/
        data = await getGoodsDiscards(params);
      }
      setItems(toInventoryList(data));
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listTab, storeId, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  function openConfirm(action, row) {
    setConfirmAction(action);
    setConfirmTarget(row);
    if (action === 'reject') setRejectReason('');
  }

  function closeConfirm() {
    setConfirmTarget(null);
    setConfirmAction(null);
    setRejectReason('');
  }

  async function runConfirm() {
    if (!confirmTarget || !confirmAction) return;
    const required = CONFIRM_ACTION_PERMISSION[confirmAction];
    if (required && !can(required)) return;
    setActing(true);
    try {
      const id = confirmTarget.id;
      if (confirmAction === 'approve') {
        // API: POST /inventory/goods-discard/{id}/approve/
        await approveGoodsDiscard(id);
        toast.success('Goods discard approved — inventory reduced');
      } else if (confirmAction === 'reject') {
        // API: POST /inventory/goods-discard/{id}/reject/
        await rejectGoodsDiscard(id, { rejection_reason: rejectReason.trim() });
        toast.success('Goods discard rejected');
      } else {
        // API: DELETE /inventory/goods-discard/{id}/
        await cancelGoodsDiscard(id);
        toast.success('Goods discard cancelled');
      }
      closeConfirm();
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  const statusChipMinWidth = useMemo(() => {
    const labels = items.map(r => (r.status ? String(r.status).replace(/_/g, ' ') : '')).filter(Boolean);
    if (!labels.length) return undefined;
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 7 + 20);
  }, [items]);

  const assignedStores = filterStoresByAssignment(stores, myStoreIds);
  const storeOptions = myStoreIds.length
    ? assignedStores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) }))
    : [
      { value: '', label: 'All stores' },
      ...stores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) })),
    ];

  // Empty until getStores() lands — better a blank than the bare UUID storeName() falls
  // back to.
  const lockedStoreLabel = storeOptions.find(o => o.value === myStoreId)?.label || '';

  const columns = useMemo(() => [
    { field: 'discard_number', headerName: 'Discard #', width: 170 },
    {
      field: 'store_id',
      headerName: 'Store',
      width: 180,
      valueFormatter: p => storeName(p.value),
    },
    {
      field: 'discard_date',
      headerName: 'Discard date',
      width: 120,
      valueFormatter: p => formatDate(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: p => <StatusChip status={p.value} minWidth={statusChipMinWidth} />,
    },
    {
      field: 'lines',
      headerName: 'Lines',
      width: 80,
      valueGetter: p => Array.isArray(p.row.lines) ? p.row.lines.length : (p.row.line_count ?? '—'),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 140,
      valueFormatter: p => formatDateTime(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => history.push(`${base}/${p.row.id}`)}
              sx={{ color: IPD_COLORS.primary }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.status === 'DRAFT' && (
            <>
              {can(INV_ACTIONS.DISCARD_EDIT) && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => history.push(`${base}/${p.row.id}/edit`)}
                    sx={{ color: IPD_COLORS.info.text }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {can(INV_ACTIONS.DISCARD_APPROVE) && (
                <Tooltip title="Approve">
                  <IconButton
                    size="small"
                    onClick={() => openConfirm('approve', p.row)}
                    sx={{ color: IPD_COLORS.success.text }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {can(INV_ACTIONS.DISCARD_REJECT) && (
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    onClick={() => openConfirm('reject', p.row)}
                    sx={{ color: IPD_COLORS.warning.text }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {can(INV_ACTIONS.DISCARD_CANCEL) && (
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={() => openConfirm('cancel', p.row)}
                    sx={{ color: IPD_COLORS.error.text }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ], [base, history, statusChipMinWidth, storeName, can]);

  const confirmTitle = confirmAction === 'approve'
    ? 'Approve Goods Discard'
    : confirmAction === 'reject'
      ? 'Reject Goods Discard'
      : 'Cancel Goods Discard';

  const confirmDescription = confirmAction === 'approve'
    ? `Approve "${confirmTarget?.discard_number}"? Stock will be permanently reduced.`
    : confirmAction === 'reject'
      ? `Reject "${confirmTarget?.discard_number}"? No stock change will occur.`
      : `Cancel "${confirmTarget?.discard_number}"? This cannot be undone.`;

  return (
    <Box>
      <SectionCard
        title="Goods Discard Records"
        subtitle="Draft discards await approval before stock is reduced"
        actions={can(INV_ACTIONS.DISCARD_CREATE) ? (
          <InvButton size="small" startIcon={<AddIcon />} onClick={() => history.push(`${base}/new`)}>
            New Goods Discard
          </InvButton>
        ) : null}
      >
        <InvTabBar
          tabs={LIST_TABS}
          activeTab={listTab}
          onChange={setListTab}
          ariaLabel="Goods discard list filters"
          sx={{ mb: 1.5 }}
        />

        <FilterToolbar>
          {storeLocked ? (
            <FilterToolbar.StaticValue label="Store" width={220} value={lockedStoreLabel} />
          ) : (
            <FilterToolbar.Filter
              label="Store"
              width={220}
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              options={storeOptions}
            />
          )}
          {listTab === 'all' && (
            <FilterToolbar.Filter
              label="Status"
              width={150}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              options={[
                { value: '', label: 'All statuses' },
                ...GOODS_DISCARD_STATUSES.map(s => ({ value: s, label: s })),
              ]}
            />
          )}
          <FilterToolbar.DateFilter
            label="From"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
          <FilterToolbar.DateFilter
            label="To"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
          />
        </FilterToolbar>

        <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1 }}>
          {items.length} record{items.length === 1 ? '' : 's'}
        </Typography>

        <InvDataGrid
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={r => r.id}
          emptyMessage={listTab === 'pending' ? 'No pending goods discards.' : 'No goods discards found.'}
        />
      </SectionCard>

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={confirmTitle}
        description={confirmDescription}
        destructive={confirmAction === 'cancel'}
        requireReason={confirmAction === 'reject'}
        reasonLabel="Rejection reason"
        reasonValue={rejectReason}
        onReasonChange={setRejectReason}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={closeConfirm}
        confirmLabel={
          confirmAction === 'approve' ? 'Approve'
            : confirmAction === 'reject' ? 'Reject'
              : 'Cancel discard'
        }
      />
    </Box>
  );
}
